import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Volume2, 
  Play, 
  Settings, 
  Radio,
  Loader2,
  Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import TankAvatar from './TankAvatar';
import DaisyAvatar from './DaisyAvatar';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Voice presets with descriptions
const TANK_VOICES = {
  adam: { name: "Adam", desc: "Deep, authoritative" },
  arnold: { name: "Arnold", desc: "Clear, technical" },
  clyde: { name: "Clyde", desc: "War veteran vibe" },
  dave: { name: "Dave", desc: "Conversational" },
  fin: { name: "Fin", desc: "Wise, storyteller" },
  drew: { name: "Drew", desc: "Well-rounded" }
};

const DAISY_VOICES = {
  rachel: { name: "Rachel", desc: "Professional" },
  domi: { name: "Domi", desc: "Strong, assertive" },
  bella: { name: "Bella", desc: "Soft, soothing" },
  elli: { name: "Elli", desc: "Expressive" },
  grace: { name: "Grace", desc: "Warm, southern" },
  charlotte: { name: "Charlotte", desc: "Calm, clear" }
};

const MOODS = {
  calm: { name: "Calm", desc: "Steady, measured" },
  urgent: { name: "Urgent", desc: "Fast, emphatic" },
  serious: { name: "Serious", desc: "Grave, authoritative" },
  friendly: { name: "Friendly", desc: "Warm, conversational" },
  commanding: { name: "Commanding", desc: "Strong, directive" }
};

const VoiceSettingsPanel = ({ onClose }) => {
  const { token } = useAuth();
  const [tankVoice, setTankVoice] = useState('clyde');
  const [daisyVoice, setDaisyVoice] = useState('rachel');
  const [defaultMood, setDefaultMood] = useState('calm');
  const [previewLoading, setPreviewLoading] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Load saved settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/voice/settings`, { 
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data) {
          setTankVoice(res.data.tank_voice || 'clyde');
          setDaisyVoice(res.data.daisy_voice || 'rachel');
          setDefaultMood(res.data.default_mood || 'calm');
        }
      } catch (e) {
        console.error('Failed to load voice settings:', e);
      }
    };
    if (token) loadSettings();
  }, [token]);

  // Preview voice
  const previewVoice = useCallback(async (character, voicePreset, mood) => {
    setPreviewLoading(`${character}-${voicePreset}`);
    try {
      const res = await axios.post(
        `${API_URL}/api/voice/preview`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { character, voice_preset: voicePreset, mood }
        }
      );
      
      if (res.data.audio_url) {
        const audio = new Audio(res.data.audio_url);
        audio.play();
      }
    } catch (e) {
      console.error('Preview failed:', e);
    } finally {
      setPreviewLoading(null);
    }
  }, [token]);

  // Save settings
  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/voice/settings/save`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { 
            tank_voice: tankVoice, 
            daisy_voice: daisyVoice, 
            default_mood: defaultMood 
          }
        }
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error('Save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Tank's Voice */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <TankAvatar size="md" showTooltip={false} />
          <div>
            <h4 className="font-semibold text-slate-100">Tank's Voice</h4>
            <p className="text-xs text-slate-500">For casual intel and low/medium alerts</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(TANK_VOICES).map(([key, voice]) => (
            <div
              key={key}
              onClick={() => setTankVoice(key)}
              className={`p-3 rounded-lg border text-left transition-all cursor-pointer ${
                tankVoice === key 
                  ? 'border-teal-500 bg-teal-500/10' 
                  : 'border-slate-700 hover:border-slate-600'
              }`}
              data-testid={`tank-voice-${key}`}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setTankVoice(key)}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-200">{voice.name}</span>
                {tankVoice === key && <Check className="w-4 h-4 text-teal-400" />}
              </div>
              <p className="text-xs text-slate-500 mt-1">{voice.desc}</p>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 h-7 text-xs text-teal-400 hover:text-teal-300 p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  previewVoice('tank', key, defaultMood);
                }}
                disabled={previewLoading === `tank-${key}`}
              >
                {previewLoading === `tank-${key}` ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <Play className="w-3 h-3 mr-1" />
                )}
                Preview
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Daisy's Voice */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <DaisyAvatar size="md" showTooltip={false} />
          <div>
            <h4 className="font-semibold text-slate-100">Daisy's Voice</h4>
            <p className="text-xs text-slate-500">For critical alerts and detailed briefings</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(DAISY_VOICES).map(([key, voice]) => (
            <div
              key={key}
              onClick={() => setDaisyVoice(key)}
              className={`p-3 rounded-lg border text-left transition-all cursor-pointer ${
                daisyVoice === key 
                  ? 'border-amber-500 bg-amber-500/10' 
                  : 'border-slate-700 hover:border-slate-600'
              }`}
              data-testid={`daisy-voice-${key}`}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setDaisyVoice(key)}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-200">{voice.name}</span>
                {daisyVoice === key && <Check className="w-4 h-4 text-amber-400" />}
              </div>
              <p className="text-xs text-slate-500 mt-1">{voice.desc}</p>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 h-7 text-xs text-amber-400 hover:text-amber-300 p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  previewVoice('daisy', key, defaultMood);
                }}
                disabled={previewLoading === `daisy-${key}`}
              >
                {previewLoading === `daisy-${key}` ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <Play className="w-3 h-3 mr-1" />
                )}
                Preview
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Default Mood */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">
            <Radio className="w-5 h-5 text-slate-400" />
          </div>
          <div>
            <h4 className="font-semibold text-slate-100">Default Mood</h4>
            <p className="text-xs text-slate-500">Sets the tone for routine communications</p>
          </div>
        </div>
        
        <Select value={defaultMood} onValueChange={setDefaultMood}>
          <SelectTrigger className="w-full bg-slate-800 border-slate-700">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            {Object.entries(MOODS).map(([key, mood]) => (
              <SelectItem key={key} value={key}>
                <div className="flex flex-col">
                  <span>{mood.name}</span>
                  <span className="text-xs text-slate-500">{mood.desc}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Save Button */}
      <Button 
        onClick={saveSettings}
        className="w-full bg-teal-600 hover:bg-teal-500"
        disabled={saving}
        data-testid="save-voice-settings-btn"
      >
        {saving ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : saved ? (
          <Check className="w-4 h-4 mr-2" />
        ) : (
          <Settings className="w-4 h-4 mr-2" />
        )}
        {saved ? 'Saved!' : 'Save Preferences'}
      </Button>

      <p className="text-xs text-slate-500 text-center">
        Voice preferences are saved to your account and sync across devices.
      </p>
    </div>
  );
};

// Voice Settings Dialog Trigger
export const VoiceSettingsButton = () => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 text-slate-400 hover:text-slate-200"
          title="Voice Settings"
        >
          <Settings className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-slate-100 flex items-center gap-2">
            <Volume2 className="w-5 h-5 text-teal-400" />
            Voice Settings
          </DialogTitle>
        </DialogHeader>
        <VoiceSettingsPanel onClose={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );
};

export default VoiceSettingsPanel;
